/**
 * Sprint T-17.1 · Notification Center · React Query hooks.
 *
 * 1 endpoint = 1 hook. NO derived state. NO composition of endpoints.
 * Auto-refresh policy (§14 approved):
 *   info               → 30s
 *   deliveries         → manual (staleTime 10s)
 *   dlq                → manual (staleTime 10s)
 *   templates          → 5min stale (rarely changes)
 *   providers_health   → 30s stale + optional 60s poll toggle
 *   preferences/me     → 5min stale
 *   webhook_endpoints  → 1min stale
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "../services/notificationsApi";

const TEN_SEC = 10 * 1000;
const THIRTY_SEC = 30 * 1000;
const ONE_MIN = 60 * 1000;
const FIVE_MIN = 5 * 60 * 1000;

const baseOpts = {
  staleTime: TEN_SEC,
  gcTime: FIVE_MIN,
  retry: 1,
  refetchOnWindowFocus: false,
};

// ---------------------------------------------------------------------------
// 1. Dispatcher info · 30s poll
// ---------------------------------------------------------------------------

export const useNotificationsInfo = () =>
  useQuery({
    queryKey: ["notifications.info"],
    queryFn: () => notificationsApi.info().then((r) => r.data),
    ...baseOpts,
    staleTime: THIRTY_SEC,
    refetchInterval: THIRTY_SEC,
  });

// ---------------------------------------------------------------------------
// 2-3. Deliveries · list + detail
// ---------------------------------------------------------------------------

export const useDeliveries = (params = {}) =>
  useQuery({
    queryKey: ["notifications.deliveries", params],
    queryFn: () => notificationsApi.listDeliveries(params).then((r) => r.data),
    ...baseOpts,
  });

export const useDeliveryDetail = (id) =>
  useQuery({
    queryKey: ["notifications.delivery", id],
    queryFn: () => notificationsApi.getDelivery(id).then((r) => r.data),
    ...baseOpts,
    enabled: Boolean(id),
    staleTime: THIRTY_SEC,
  });

// ---------------------------------------------------------------------------
// 4-5. Intents (list + replay)
// ---------------------------------------------------------------------------

export const useNotificationIntents = (params = {}) =>
  useQuery({
    queryKey: ["notifications.intents", params],
    queryFn: () => notificationsApi.listIntents(params).then((r) => r.data),
    ...baseOpts,
  });

export const useReplayIntent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => notificationsApi.replayIntent(id).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications.intents"] });
      qc.invalidateQueries({ queryKey: ["notifications.deliveries"] });
    },
  });
};

// ---------------------------------------------------------------------------
// 6-8. DLQ
// ---------------------------------------------------------------------------

export const useNotificationDlq = (params = {}) =>
  useQuery({
    queryKey: ["notifications.dlq", params],
    queryFn: () => notificationsApi.listDlq(params).then((r) => r.data),
    ...baseOpts,
  });

export const useNotifDlqRetry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => notificationsApi.retryDlq(id).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications.dlq"] });
      qc.invalidateQueries({ queryKey: ["notifications.intents"] });
      qc.invalidateQueries({ queryKey: ["notifications.deliveries"] });
    },
  });
};

export const useNotifDlqDiscard = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => notificationsApi.discardDlq(id).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications.dlq"] }),
  });
};

// ---------------------------------------------------------------------------
// 9-11. Templates (list + detail + overrides · read-only)
// ---------------------------------------------------------------------------

export const useTemplates = () =>
  useQuery({
    queryKey: ["notifications.templates"],
    queryFn: () => notificationsApi.listTemplates().then((r) => r.data),
    ...baseOpts,
    staleTime: FIVE_MIN,
  });

export const useTemplateDetail = (id) =>
  useQuery({
    queryKey: ["notifications.template", id],
    queryFn: () => notificationsApi.getTemplate(id).then((r) => r.data),
    ...baseOpts,
    enabled: Boolean(id),
    staleTime: FIVE_MIN,
  });

export const useTemplateOverrides = (id, tenant_id) =>
  useQuery({
    queryKey: ["notifications.overrides", id, tenant_id || null],
    queryFn: () => notificationsApi.listOverrides(id, tenant_id).then((r) => r.data),
    ...baseOpts,
    enabled: Boolean(id),
  });

// ---------------------------------------------------------------------------
// 12. Providers health · optional poll
// ---------------------------------------------------------------------------

export const useProvidersHealth = ({ poll = null } = {}) =>
  useQuery({
    queryKey: ["notifications.providers.health"],
    queryFn: () => notificationsApi.providersHealth().then((r) => r.data),
    ...baseOpts,
    staleTime: THIRTY_SEC,
    refetchInterval: poll || false,
  });

// ---------------------------------------------------------------------------
// 13-14. Preferences (my own)
// ---------------------------------------------------------------------------

export const useMyPreferences = () =>
  useQuery({
    queryKey: ["notifications.preferences.me"],
    queryFn: () => notificationsApi.myPreferences().then((r) => r.data),
    ...baseOpts,
    staleTime: FIVE_MIN,
  });

export const useUpdateMyPreferences = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => notificationsApi.updateMyPreferences(body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications.preferences.me"] }),
  });
};

// ---------------------------------------------------------------------------
// 15-16. Webhook endpoints
// ---------------------------------------------------------------------------

export const useWebhookEndpoints = (params = {}) =>
  useQuery({
    queryKey: ["notifications.webhookEndpoints", params],
    queryFn: () => notificationsApi.listWebhookEndpoints(params).then((r) => r.data),
    ...baseOpts,
    staleTime: ONE_MIN,
  });

export const useCreateWebhookEndpoint = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => notificationsApi.createWebhookEndpoint(body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications.webhookEndpoints"] }),
  });
};

export const useDeleteWebhookEndpoint = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => notificationsApi.deleteWebhookEndpoint(id).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications.webhookEndpoints"] }),
  });
};

// ============================================================================
// Sprint T-18 · Notification Studio · additive hooks (superadmin-only)
// ============================================================================

// H-1 · render preview mutation (Playground live + Studio button)
export const useRenderPreview = () =>
  useMutation({
    mutationFn: (body) =>
      notificationsApi.studio.renderPreview(body).then((r) => r.data),
  });

// H-2 · validate override
export const useValidateTemplate = () =>
  useMutation({
    mutationFn: (body) =>
      notificationsApi.studio.validate(body).then((r) => r.data),
  });

// H-3 · extract variables from arbitrary content
export const useExtractVariables = () =>
  useMutation({
    mutationFn: (body) =>
      notificationsApi.studio.extractVariables(body).then((r) => r.data),
  });

// T-18 · CRUD overrides (Q8=c · unified within Templates tab)
export const useUpsertTemplateOverride = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ template_id, body }) =>
      notificationsApi.upsertOverride(template_id, body).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["notifications.templateOverrides", vars.template_id] });
    },
  });
};

export const useDeleteTemplateOverride = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ template_id, override_id }) =>
      notificationsApi.deleteOverride(template_id, override_id).then((r) => r.data),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["notifications.templateOverrides", vars.template_id] });
    },
  });
};

// H-4 · playground history (global · Q7=b · 60s stale)
export const usePlaygroundHistory = (params = {}) =>
  useQuery({
    queryKey: ["notifications.studio.playgroundHistory", params],
    queryFn: () =>
      notificationsApi.studio.playgroundHistory(params).then((r) => r.data),
    ...baseOpts,
    staleTime: ONE_MIN,
  });

// H-5 · persist a history entry
export const useSavePlaygroundEntry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      notificationsApi.studio.savePlaygroundEntry(body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications.studio.playgroundHistory"] }),
  });
};

// Extra · reuse the T-17.1 test-send endpoint via a T-18-aware hook
// (invalidates deliveries + history on success so the Playground refreshes both)
export const useTestSend = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => notificationsApi.testSend(body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications.deliveries"] });
      qc.invalidateQueries({ queryKey: ["notifications.studio.playgroundHistory"] });
    },
  });
};

// ---- Fase 4 · Analytics ----
export const useAnalyticsSummary = (params = { range: "24h" }) =>
  useQuery({
    queryKey: ["notifications.studio.analytics.summary", params],
    queryFn: () =>
      notificationsApi.studio.analyticsSummary(params).then((r) => r.data),
    ...baseOpts,
    staleTime: ONE_MIN,
    refetchInterval: ONE_MIN,
  });

export const useAnalyticsThroughput = (params = { range: "24h", bucket: "1h" }) =>
  useQuery({
    queryKey: ["notifications.studio.analytics.throughput", params],
    queryFn: () =>
      notificationsApi.studio.analyticsThroughput(params).then((r) => r.data),
    ...baseOpts,
    staleTime: ONE_MIN,
  });

export const useAnalyticsErrors = (params = { groupBy: "provider", range: "24h" }) =>
  useQuery({
    queryKey: ["notifications.studio.analytics.errors", params],
    queryFn: () =>
      notificationsApi.studio.analyticsErrors(params).then((r) => r.data),
    ...baseOpts,
    staleTime: ONE_MIN,
  });

export const useAnalyticsLatency = (params = { range: "24h" }) =>
  useQuery({
    queryKey: ["notifications.studio.analytics.latency", params],
    queryFn: () =>
      notificationsApi.studio.analyticsLatency(params).then((r) => r.data),
    ...baseOpts,
    staleTime: ONE_MIN,
  });

// ---- Fase 4 · Legacy migration ----
export const useLegacySummary = (params = { days: 30 }) =>
  useQuery({
    queryKey: ["notifications.studio.legacy.summary", params],
    queryFn: () =>
      notificationsApi.studio.legacySummary(params).then((r) => r.data),
    ...baseOpts,
    staleTime: 5 * ONE_MIN,
  });

export const useLegacyCallsites = () =>
  useQuery({
    queryKey: ["notifications.studio.legacy.callsites"],
    queryFn: () => notificationsApi.studio.legacyCallsites().then((r) => r.data),
    ...baseOpts,
    staleTime: 5 * ONE_MIN,
  });
